/**
 * Textes du parcours de consentement Watt Watchers (DEV-2274).
 *
 * POINT UNIQUE : chaque texte d'autorisation est écrit ICI, une seule fois,
 * sous forme de segments. Les composants l'affichent segment par segment
 * (`ConsentSegments.vue`, interpolation Vue, jamais `v-html`) et le `text`
 * envoyé à l'API pour les accords DPE et FOST est produit par `toPlainText`
 * à partir des MÊMES segments : ce qui est enregistré est ce qui est affiché,
 * sur une seule ligne, espaces normalisés, dates injectées.
 *
 * Les textes reprennent mot pour mot la spécification validée (dossier v1.1,
 * spécification d'implémentation v1.0 du 01/10/2026, sections 2.1 à 2.4).
 * Toute modification d'un texte se fait ici et nulle part ailleurs.
 */

/**
 * Date de fin de toutes les autorisations du formulaire : fin du programme
 * Watt Watchers (31/12/2027) plus six mois.
 *
 * La SOURCE DE VÉRITÉ est api-ww (`WW_CONSENT_DATE_FIN`), qui la renvoie dans
 * le dossier sous `consent_date_fin`. Cette valeur n'est qu'un REPLI, pour un
 * dossier servi par une api-ww qui ne la renvoie pas encore : elle doit rester
 * identique à celle d'api-ww.
 */
export const WW_CONSENT_DATE_FIN_DEFAUT = "2028-06-30"

/** Identité de Homeys telle qu'elle figure dans les textes. */
const HOMEYS_SIREN = "Homeys (SAS, SIREN 839 582 335)"

/** Date de début affichée tant que le ménage ne l'a pas saisie. */
export const DEBUT_INCONNU = "--/--/----"

/** Adresse de retrait des autorisations. */
export const DPO_EMAIL = "dpo@homeys.io"

/** Un morceau de texte, éventuellement en gras ou en lien. */
export type ConsentSegment = {
    text: string
    strong?: boolean
    href?: string
}

/** Un bloc de texte avec une liste à puces facultative. */
export type ConsentBlock = {
    lead: ConsentSegment[]
    items?: string[]
    after?: ConsentSegment[]
}

/**
 * Rend des segments (ou des blocs) en une seule ligne : espaces normalisés,
 * sans retour à la ligne ni indentation (le défaut de l'ancien
 * `TEXT_CONSENT`, enregistré sur 599 caractères avec ses indentations).
 */
export function toPlainText(
    ...parts: (ConsentSegment[] | ConsentBlock | string | undefined)[]
): string {
    const chunks: string[] = []
    for (const part of parts) {
        if (!part) continue
        if (typeof part === "string") {
            chunks.push(part)
        } else if (Array.isArray(part)) {
            chunks.push(part.map((s) => s.text).join(""))
        } else {
            chunks.push(part.lead.map((s) => s.text).join(""))
            if (part.items) chunks.push(...part.items)
            if (part.after) chunks.push(part.after.map((s) => s.text).join(""))
        }
    }
    return chunks.join(" ").replace(/\s+/g, " ").trim()
}

/**
 * Jour calendaire au format AAAA-MM-JJ, lu dans le fuseau du navigateur.
 *
 * Normalise les deux formes que prend une date de début de contrat dans le
 * formulaire : la `Date` du sélecteur (minuit local) et la chaîne ISO rendue
 * par l'API (`2025-08-31T22:00:00.000Z` pour un 01/09 saisi à Paris, ou
 * `2025-09-01T00:00:00.000Z` pour un 01/09 envoyé en AAAA-MM-JJ). Une chaîne
 * déjà au format AAAA-MM-JJ est rendue telle quelle.
 *
 * @returns le jour, ou `null` si la valeur est vide ou illisible
 */
export function toIsoDay(value: unknown): string | null {
    if (value === null || value === undefined || value === "") return null
    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
        return value
    }
    const date =
        value instanceof Date ? value : new Date(value as string | number)
    if (Number.isNaN(date.getTime())) return null
    const y = date.getFullYear()
    const m = String(date.getMonth() + 1).padStart(2, "0")
    const d = String(date.getDate()).padStart(2, "0")
    return `${y}-${m}-${d}`
}

/**
 * Date au format JJ/MM/AAAA (jour lu comme `toIsoDay`).
 * @returns la date formatée, ou `null` si la valeur est vide ou illisible
 */
export function toFrDay(value: unknown): string | null {
    const iso = toIsoDay(value)
    if (!iso) return null
    const [y, m, d] = iso.split("-")
    return `${d}/${m}/${y}`
}

/**
 * Date de fin des autorisations, au format JJ/MM/AAAA, lue dans le dossier
 * (`consent_date_fin`, fourni par api-ww) avec repli sur la même valeur codée.
 */
export function consentDateFin(record?: { consent_date_fin?: string } | null) {
    return (
        toFrDay(record?.consent_date_fin) ??
        (toFrDay(WW_CONSENT_DATE_FIN_DEFAUT) as string)
    )
}

// =============================================================
// 2.1 Étape gaz
// =============================================================

export const GAZ_TOGGLE_OUVRIR = "Voir le détail de l'autorisation"
export const GAZ_TOGGLE_FERMER = "Masquer le détail"
export const GAZ_MESSAGE_MANQUANT =
    "Donnez votre autorisation pour confirmer votre compteur."
export const GAZ_BOUTON = "Je confirme mon compteur"

/** Phrase à côté de la case, visible repliée. */
export function gazPhrase({ pce, fin }: { pce: string; fin: string }) {
    return [
        {
            text: "Je suis titulaire du contrat de gaz de ce logement et j'autorise GRDF à transmettre à Homeys",
            strong: true,
        },
        { text: ", dans le cadre du programme " },
        { text: "Watt Watchers", strong: true },
        {
            text: `, les données de mon compteur de gaz${pce?.trim() ? ` ${pce.trim()}` : ""}, jusqu'au ${fin}.`,
        },
    ] as ConsentSegment[]
}

/** Détail déplié. */
export function gazDetail({
    debut,
    fin,
    titulaire,
    cp,
}: {
    debut: string
    fin: string
    titulaire: string
    cp: string
}): ConsentBlock {
    return {
        lead: [{ text: `${HOMEYS_SIREN} reçoit de GRDF :` }],
        items: [
            "mes consommations mensuelles (données publiées) et journalières (données informatives) ;",
            "les données techniques et contractuelles de mon compteur ;",
            `à partir du ${debut}, et jusqu'au ${fin}, date de fin de cette autorisation.`,
        ],
        after: [
            {
                text: `Titulaire : ${titulaire}, ${cp}. Ces données servent à mesurer la performance réelle de ma rénovation. Elles sont transmises au CSTB de manière pseudonymisée. Je peux retirer cette autorisation à tout moment en écrivant à ${DPO_EMAIL}.`,
            },
        ],
    }
}

/** Annonce du courriel de GRDF, hors du bloc replié, toujours visible. */
export const GAZ_ANNONCE: ConsentSegment[] = [
    { text: "GRDF va vous envoyer un courriel", strong: true },
    {
        text: " pour confirmer cette demande d'accès de Homeys. Pensez à la valider dès réception : sans votre confirmation, GRDF ne nous transmet pas vos données de gaz.",
    },
]

// =============================================================
// 2.2 Étape électricité
// =============================================================

export const ELEC_TOGGLE_OUVRIR = "Voir les deux autorisations"
export const ELEC_TOGGLE_FERMER = "Masquer le détail"
export const ELEC_MESSAGE_MANQUANT =
    "Donnez vos deux autorisations pour continuer."
export const ELEC_BOUTON = "Je confirme"

/** Case maître, visible repliée. */
export function elecPhrase({ fin }: { fin: string }) {
    return [
        {
            text: "Je suis titulaire du contrat d'électricité de ce logement et j'autorise",
            strong: true,
        },
        { text: ", dans le cadre du programme " },
        { text: "Watt Watchers", strong: true },
        {
            text: `, Enedis à transmettre chaque jour à Homeys les données de mon compteur d'électricité, y compris ma courbe de charge, et Homeys à récupérer les informations sur mes travaux et mon DPE, jusqu'au ${fin}.`,
        },
    ] as ConsentSegment[]
}

/** Première autorisation dépliée : Enedis. */
export function elecEnedis({ fin }: { fin: string }): ConsentBlock {
    return {
        lead: [
            { text: "J'autorise Enedis à transmettre à Homeys", strong: true },
            {
                text: " (SAS, SIREN 839 582 335) les données de mon compteur d'électricité :",
            },
        ],
        items: [
            "ma consommation journalière et mes index quotidiens ;",
            "ma courbe de charge, c'est-à-dire ma consommation détaillée à la demi-heure, dont j'autorise la collecte par Enedis et la transmission quotidienne à Homeys ;",
            "mes puissances maximales quotidiennes, et les données techniques et contractuelles de mon compteur ;",
            `l'historique disponible chez Enedis, puis jusqu'au ${fin}, date de fin de cette autorisation.`,
        ],
    }
}

/** Seconde autorisation dépliée : travaux, DPE et météo (accords DPE/FOST). */
export function elecTravaux({ fin }: { fin: string }) {
    return [
        { text: "J'autorise Homeys à récupérer", strong: true },
        {
            text: ` auprès de mon financeur ou accompagnateur les informations sur mes travaux (dates, nature des travaux, données nécessaires au calcul des kWh cumac) et le DPE de mon logement, et à associer à mon adresse les données météo locales, jusqu'au ${fin}.`,
        },
    ] as ConsentSegment[]
}

/** Paragraphe commun sous les deux autorisations dépliées. */
export const ELEC_APRES: ConsentSegment[] = [
    {
        text: `Ces données servent à mesurer la performance réelle de ma rénovation. Elles sont transmises au CSTB de manière pseudonymisée. Je peux retirer ces autorisations à tout moment en écrivant à ${DPO_EMAIL}.`,
    },
]

/**
 * Texte enregistré sur les accords DPE et FOST : la phrase réellement
 * affichée ET cochée qui porte cet accord. Repliée, c'est la case maître ;
 * dépliée, c'est la case « travaux, DPE et météo ».
 */
export function elecTexteDpeFost({
    fin,
    deplie,
}: {
    fin: string
    deplie: boolean
}): string {
    return deplie
        ? toPlainText(elecTravaux({ fin }))
        : toPlainText(elecPhrase({ fin }))
}

// =============================================================
// 2.3 Reçu
// =============================================================

export const RECU_BOUTON = "Continuer"

/** Ligne du reçu, avec ou sans date connue. */
export function recuTexte(date?: string | null) {
    return date
        ? `Vous avez déjà donné votre autorisation le ${date}.`
        : "Vous avez déjà donné votre autorisation."
}

// =============================================================
// 2.4 Fenêtres « En savoir plus »
// =============================================================

export type DialogParagraph = {
    title: string
    blocks: ConsentBlock[]
}

const POURQUOI_PDL =
    "Pour mesurer les économies d'énergie réellement obtenues après vos travaux, et contribuer à l'amélioration des politiques publiques de rénovation."
const POURQUOI_PCE =
    "Si vous utilisez (ou si vous utilisiez avant les travaux) le gaz naturel pour vous chauffer ou pour la production d'eau chaude sanitaire, il est important de mesurer également l’évolution de cette consommation pour évaluer les économies réelles après travaux."

const PERIODE_CSTB =
    "Pour l'étude, seule la consommation d'un an avant et d'un an après vos travaux est transmise au CSTB, de manière pseudonymisée."

/** Rubriques de la fenêtre « En savoir plus » (gaz `pce`, électricité `pdl`). */
export function dialogParagraphs({
    type,
    fin,
    debut,
}: {
    type: "pdl" | "pce"
    fin: string
    /** Date de début de contrat saisie (gaz), JJ/MM/AAAA. */
    debut?: string | null
}): DialogParagraph[] {
    const gaz = type === "pce"
    const gestionnaire = gaz ? "GRDF" : "Enedis"

    const donnees: ConsentBlock[] = gaz
        ? [
              {
                  // Liste exacte du détail 2.1.
                  lead: [{ text: `${HOMEYS_SIREN} reçoit de GRDF :` }],
                  items: gazDetail({
                      debut: debut || DEBUT_INCONNU,
                      fin,
                      titulaire: "",
                      cp: "",
                  }).items,
              },
          ]
        : [
              {
                  lead: [
                      {
                          text: "J'autorise Enedis à transmettre à Homeys (SAS, SIREN 839 582 335) les données de mon compteur d'électricité :",
                      },
                  ],
                  // Liste exacte du détail 2.2.
                  items: elecEnedis({ fin }).items,
              },
              { lead: [{ text: toPlainText(elecTravaux({ fin })) }] },
          ]

    return [
        {
            title: "Pourquoi ces données ?",
            blocks: [{ lead: [{ text: gaz ? POURQUOI_PCE : POURQUOI_PDL }] }],
        },
        { title: "Quelles données sont demandées ?", blocks: donnees },
        {
            title: "Sur quelle période ?",
            blocks: [
                {
                    lead: [
                        {
                            text: gaz
                                ? `Depuis la date de début de votre contrat de gaz, jusqu'au ${fin}, fin du programme Watt Watchers plus six mois.`
                                : `L'historique disponible chez Enedis, puis jusqu'au ${fin}, fin du programme Watt Watchers plus six mois.`,
                        },
                    ],
                },
                { lead: [{ text: PERIODE_CSTB }] },
            ],
        },
        {
            title: "Qui traite mes données ?",
            blocks: [
                {
                    lead: [
                        {
                            text: `${gestionnaire} transmet vos données à Homeys. ${HOMEYS_SIREN} les utilise pour l'étude Watt Watchers. Le CSTB, organisme public, reçoit des données pseudonymisées.`,
                        },
                    ],
                },
            ],
        },
        {
            title: "Comment retirer mon autorisation ?",
            blocks: [
                {
                    lead: [
                        { text: "À tout moment, en écrivant à " },
                        { text: DPO_EMAIL, href: `mailto:${DPO_EMAIL}` },
                        {
                            text: `${gaz ? ", ou en refusant l'accès depuis le courriel de GRDF" : ""}. Nous arrêtons alors la collecte et ne redemandons pas l'accès, sauf si vous nous redonnez vous-même votre autorisation.`,
                        },
                    ],
                },
            ],
        },
    ]
}
